import React, { useState, useEffect, useRef } from "react";
import AuthChoice from "../../components/AuthChoice";
import AuthDetailForm from "../../components/AuthDetailForm";
import { makePost } from "../../utils/fetchUtils";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { GOOGLE_LOGIN, SEND_OTP, SIGNUP, VALIDATE_OTP } from "../../utils/constants";
import { setTimeout } from "timers/promises";
import { ErrorObj } from "../../utils/types";
import nclude from "../../assets/nclude.svg";
import sticker1 from "../../assets/sticker1.svg";
import curve1 from "../../assets/curve.svg";
import woman1 from "../../assets/woman1.png";
import greaterThan from "../../assets/greaterthan.svg";
import { CredentialResponse, GoogleLogin } from "@react-oauth/google";

export type AuthChoiceType = "user" | "org" | "choice";

const Signup = () => {
  useEffect(() => {
    if (localStorage.getItem("token")) {
      navigate("/");
    }
    document.title = "Signup - Nclude Portal";
  }, []);

  const navigate = useNavigate();

  const mainRef = useRef<HTMLInputElement>(null);
    const location = useLocation();

  const [authChoice, setAuthChoice] = useState<AuthChoiceType>("user");
  const [userName, setUserName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [isOtpMatched, setIsOtpMatched] = useState(false);

  //0 - not verified , 1 - verification failed , 2 - verification successful
  const [emailVerified, setEmailVerified] = useState<0 | 1 | 2>(0);
  const [phoneVerified, setPhoneVerified] = useState<0 | 1 | 2>(0);

  const [password, setPassword] = useState("");
  // const [error,setError] = useState('');
  const [requestAccessLink, setRequestAccessLink] = useState(false);
  const [verifyError, setVerifyError] = useState("");
  const [error, setError] = useState<ErrorObj>({
    email: "",
    password: "",
    phone: "",
    system: "",
    confirmPassword: "",
  });

  const handleOtpValidation = async (choice: any): Promise<boolean> => {
    setVerifyError("");
    try {
      const body = {
        otp: otp,
        phoneNumber: phoneNumber,
        email: email,
      };
      if (choice === "phone") {
        body.email = "";
      } else {
        body.phoneNumber = "";
      }
      const response = await makePost(VALIDATE_OTP, JSON.stringify(body));
      if (response.success === true) {
        setIsOtpMatched(true);
        setOtpSent(false);
        if (choice === "phone") {
          setPhoneVerified(2);
        } else {
          setEmailVerified(2);
        }
        setError({});
        setVerifyError("");
        return true;
      } else {
        setIsOtpMatched(false);
        setVerifyError(response.message || "Invalid OTP. Please try again.");
        if (choice === "phone") {
          setPhoneVerified(0);
        } else {
          setEmailVerified(0);
        }
        return false;
      }
    } catch (error: any) {
      setError({});
      if (error.response && error.response.data && error.response.data.message) {
        setVerifyError(error.response.data.message);
      } else {
        setVerifyError("Invalid OTP. Please try again.");
      }
      setIsOtpMatched(false);
      if (choice === "phone") {
        setPhoneVerified(0);
      } else {
        setEmailVerified(0);
      }
      return false;
    }
  };

  const handleSendOtp = async (choice: any): Promise<boolean> => {
    setVerifyError("");
    try {
      const body = {
        email: email,
        phoneNumber: phoneNumber,
        userType: authChoice,
      };
      if (choice === "phone") {
        body.email = "";
      } else {
        body.phoneNumber = "";
      }
      const resp = await makePost(SEND_OTP, JSON.stringify(body));
      if (resp.success === true) {
        setOtpSent(true);
        setError({});
        return true;
      } else {
        setError({
          system: resp.message || "Failed to send OTP",
        });
        return false;
      }
    } catch (error: any) {
      setError({});
      if (error.response && error.response.data) {
        if (error.response.status === 401) {
          setRequestAccessLink(true);
        } else
          setError({
            system: error.response.data.message || "Failed to send OTP",
          });
      } else {
        setError({
          system: "Failed to send OTP. Please try again.",
        });
      }
      return false;
    }
    setOtp("");
  };

  const phoneValidator = () => {
    setError({});
    if (!phoneNumber) {
      setError({
        phone: "Please enter a phone number",
      });
      return false;
    }
    const regex = /^(?:\+1[-\.\s]?)?(?:\(?([2-9]\d{2})\)?[-\.\s]?)([2-9]\d{2})[-\.\s]?(\d{4})$/;
    if (!regex.test(phoneNumber)) {
      setError({
        phone: "Enter a valid US phone number",
      });
      return false;
    }
    return true;
  };

  const emailValidator = () => {
    setError({});
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!regex.test(email)) {
      setError({
        email: "Please enter a valid email address",
      });
      return false;
    }
    return true;
  };

  const [signedUpEmail, setSignedUpEmail] = useState<string>("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendStatus, setResendStatus] = useState("");

  const handleResendSignupLink = async () => {
    if (!signedUpEmail) return;
    setResendLoading(true);
    setResendStatus("");
    try {
      const resp = await makePost(SEND_OTP, JSON.stringify({ email: signedUpEmail }));
      setResendLoading(false);
      if (resp && resp.success !== false) {
        setResendStatus("Verification link resent successfully! Check your inbox.");
      } else {
        setResendStatus(resp.message || "Failed to resend link.");
      }
    } catch (err: any) {
      setResendLoading(false);
      setResendStatus("Failed to resend link. Please try again.");
    }
  };

  const [signupLoading, setSignupLoading] = useState(false);

  const handleSignup = async () => {
    setError({});
    if (email && !emailValidator()) {
      return;
    }
    if (userName === "" || password === "") {
      setError({
        system: "Please fill in all required details",
      });
      return;
    }
    if (password.length < 8) {
      setError({
        password: "Minimum length for password must be 8 characters",
      });
      return;
    }

    setSignupLoading(true);
    try {
      const body = {
        userName,
        phoneNumber,
        email,
        userType: authChoice,
        password,
        country: "usa",
      };

      const formatErrorMsg = (rawMsg: string) => {
        if (!rawMsg) return "Registration failed. Please try again.";
        return rawMsg
          .replace(/Phone Number\/Email/gi, "Email address")
          .replace(/Phone Number or Email/gi, "Email address")
          .replace(/email\/phone number/gi, "email address");
      };

      const response = await makePost(SIGNUP, JSON.stringify(body));
      if (response.success === true) {
        navigate("/verify-request", { state: { email } });
      } else {
        setError({
          system: formatErrorMsg(response.message),
        });
      }
    } catch (error: any) {
      if (error.response && error.response.data) {
        if (error.response.status === 401) {
          setRequestAccessLink(true);
          setError({});
        } else {
          const rawMsg = error.response.data.message || "Registration failed";
          const formatted = rawMsg
            .replace(/Phone Number\/Email/gi, "Email address")
            .replace(/Phone Number or Email/gi, "Email address")
            .replace(/email\/phone number/gi, "email address");
          setError({
            system: formatted,
          });
        }
      }
    } finally {
      setSignupLoading(false);
    }
  };

  const [googleLoginLoading, setGoogleLoginLoading] = useState(false);

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    setError({
      email: "",
      password: "",
      phone: "",
      system: "",
    });

    if (!credentialResponse.credential) {
      setError({
        ...error,
        system: "Google sign-in failed. Please try again.",
      });
      return;
    }

    setGoogleLoginLoading(true);
    try {
      const response = await makePost(
        GOOGLE_LOGIN,
        JSON.stringify({ credential: credentialResponse.credential, country: "usa" })
      );
      if (response.success === true) {
        localStorage.setItem("token", response.token);
        localStorage.setItem("resume", response.hasUserInfo);
        localStorage.setItem("scope", response.scope);
        localStorage.setItem("userId", response.userId);
        navigate("/");
      } else {
        setError({
          ...error,
          system: response.message || "Google sign-in failed. Please try again.",
        });
      }
    } catch (error: any) {
      setError({
        email: "",
        password: "",
        phone: "",
        system: "Google sign-in failed. Please try again.",
      });
      console.log("error while logging in with google : ", error);
    } finally {
      setGoogleLoginLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError({
      email: "",
      password: "",
      phone: "",
      system: "Google sign-in failed. Please try again.",
    });
  };
  
  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.focus();
    }
  }, [location]);

  return (
    <div className="auth-page flex justify-content-center login-form" role="main">
      <div className="logo-container">
        <div className="flex justify-center items-center h-screen w-full">
          <div className="flex flex-col items-start">
            <img src={nclude} alt="Nclude logo" className="logo mb-3" />
            <div className="flex flex-col items-start text-container">
              <img src={sticker1} alt="" aria-hidden="true" className="sticker1 mb-2" />
              <img src={curve1} alt="" aria-hidden="true" className="curve1 mb-4" />
              <h3 className="text-left font-normal">
                Resources <br />
                and opportunities for <span className="font-bold tracking-normal text-white">People with Disabilities</span>
              </h3>
            </div>
          </div>
        </div>
      </div>
      <div className="auth-form-container flex flex-col items-center">
        <div className="auth-form">
          <h1
            className="mt-12 mb-3 text-[2rem]"
            tabIndex={-1}
            ref={mainRef}
            style={{ fontWeight: 600, lineHeight: "2.375rem" }}
          >
            Welcome to Nclude Portal
          </h1>

          {signedUpEmail ? (
            <div className="p-6 bg-green-50 border border-green-200 rounded-xl text-center my-6">
              <div className="w-14 h-14 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-3 text-2xl">
                ✉️
              </div>
              <h2 className="text-xl font-bold text-green-800 mb-2">Verification Link Sent</h2>
              <p className="text-gray-700 text-sm mb-4">
                A verification link has been sent to <strong>{signedUpEmail}</strong>. Please verify your email to continue.
              </p>

              <div className="flex flex-col gap-3 items-center">
                <button
                  type="button"
                  className="text-[#5030E5] font-semibold text-sm underline hover:text-purple-800"
                  onClick={handleResendSignupLink}
                  disabled={resendLoading}
                >
                  {resendLoading ? "Resending Link..." : "Didn't get it? Resend Link"}
                </button>

                {resendStatus && (
                  <p className="text-green-700 text-xs font-semibold" role="status">
                    {resendStatus}
                  </p>
                )}

                <Link
                  to="/login"
                  className="mt-2 inline-block bg-[#4f00ec] text-white px-6 py-3 rounded-lg text-sm font-semibold hover:bg-[#3f20ca]"
                >
                  Proceed to Login
                </Link>
              </div>
            </div>
          ) : (
            <>
              <h2
                className="text-[1.875rem]"
                style={{ color: "#787486", fontWeight: 500, fontSize: "1rem", lineHeight: '1.25rem' }}
              >
                Register your account in Nclude Portal
              </h2>
              <div className="flex flex-col">
                <div className="flex justify-center mt-4" style={{ opacity: googleLoginLoading ? 0.7 : 1 }}>
                  <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={handleGoogleError}
                      text="signup_with"
                      width="360"
                  />
                </div>
                <div className="flex items-center mt-8" aria-hidden="true">
                  <div className="flex-1 border-t" style={{ borderColor: "#e2e8f0" }} />
                  <span className="mx-3" style={{ color: "#787486", fontSize: "0.875rem" }}>OR</span>
                  <div className="flex-1 border-t" style={{ borderColor: "#e2e8f0" }} />
                </div>
                {authChoice === "choice" && (
                  <AuthChoice
                    authChoice={authChoice}
                    setAuthChoice={setAuthChoice}
                  />
                )}
                {authChoice !== "choice" && (
                  <AuthDetailForm
                    handleSendOtp={handleSendOtp}
                    otp={otp}
                    otpSent={otpSent}
                    setOtp={setOtp}
                    setOtpSent={setOtpSent}
                    phoneNumber={phoneNumber}
                    setPhoneNumber={setPhoneNumber}
                    email={email}
                    setEmail={setEmail}
                    emailVerified={emailVerified}
                    setEmailVerified={setEmailVerified}
                    phoneVerified={phoneVerified}
                    setPhoneVerified={setPhoneVerified}
                    setUserName={setUserName}
                    userName={userName}
                    handleOtpValidation={handleOtpValidation}
                    handleSignup={handleSignup}
                    signupLoading={signupLoading}
                    isOtpMatched={isOtpMatched}
                    setIsOtpMatched={setIsOtpMatched}
                    password={password}
                    setPassword={setPassword}
                    error={error}
                    setError={setError}
                    verifyError={verifyError}
                    setVerifyError={setVerifyError}
                    requestAccessLink={requestAccessLink}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Signup;
